import urlConstants from '~/lib/constants/url.constants';
import { CreateGroupClassNoteRequest } from '~/lib/types/notes/CreateGroupClassNoteRequest';
import { GetGroupClassNoteByEventExternalIdResponse } from '~/lib/types/notes/GetGroupClassNoteByEventExternalIdResponse';
import { NoteResponse } from '~/lib/types/notes/NoteResponse';
import api from '~/store/services/api';
import { CacheTag } from '~/store/services/CacheTag';
import {
  EndpointKey as InquiryEndpointKey,
  inquiryApi,
} from '~/store/services/inquiry';
import { HttpRequestMethod } from '~/store';
import { NotesPaginationCacheManager } from './notesPaginationCacheManager';
import { PatientNoteListResponseType } from '~/lib/constants/AddPatientNoteModal.constants';
import { fetchInBatches, joinQueryParams } from '~/lib/util/api';

export const DELETE_NOTE_ATTACHMENT_SUCCESS_MESSAGE = 'Attachment is deleted.';

export type GetNoteListRequest = {
  patientId: string;
  filterBy?: {
    createdByProviderExternalId?: string[];
    noteStatus?: string[];
    noteType?: string[];
    isFamilyNote?: boolean;
    providerRole?: string[];
    hasAttachments?: boolean;
    appointmentExternalId?: string;
    title?: string;
    documentId?: string;
  };

  pageSize: number;
};

type GetNoteListFilterBy = {
  appointment_external_id?: string;
  'created-by-provider-external-id'?: string[];
  'has-attachments'?: boolean;
  'note-status'?: string[];
  'note-type'?: string[];
  'provider-role'?: string[];
  'is-family-note'?: boolean;
  title?: string;
  'document-id'?: string;
};

export const notesApi = api.injectEndpoints({
  endpoints: (build) => ({
    createGroupClassNote: build.mutation<
      { noteExternalId: string },
      CreateGroupClassNoteRequest
    >({
      invalidatesTags: (_result, _error, arg) => {
        const tags: Array<CacheTag | { type: CacheTag; id: string }> = [
          CacheTag.GroupClassNotesByEventExternalIds,
        ];
        if (arg.groupClassEventExternalId) {
          tags.push({
            type: CacheTag.GroupClassNotesByEventExternalIds,
            id: arg.groupClassEventExternalId,
          });
        }

        return tags;
      },
      query: (body) => ({
        body,
        method: HttpRequestMethod.POST,
        url: urlConstants.clinical_data.postGroupNote,
      }),
    }),

    createGroupSectionEventNote: build.mutation<
      { noteExternalId: string },
      CreateGroupClassNoteRequest
    >({
      invalidatesTags: (_result, _error, arg) => {
        const tags: Array<CacheTag | { type: CacheTag; id: string }> = [
          CacheTag.GroupSectionNotesByEventExternalIds,
        ];
        if (arg.groupSectionEventExternalId) {
          tags.push({
            type: CacheTag.GroupSectionNotesByEventExternalIds,
            id: arg.groupSectionEventExternalId,
          });
        }

        return tags;
      },
      query: (body) => ({
        body,
        method: HttpRequestMethod.POST,
        url: urlConstants.clinical_data.postGroupSectionEventNote,
      }),
    }),
    createNote: build.mutation<{ noteExternalId: string }, PersistNoteRequest>({
      invalidatesTags: [
        CacheTag.PatientNotes,
        CacheTag.GetNoteList,
        CacheTag.PatientAppointmentsWithNotes,
        CacheTag.ProviderDashboard,
      ],
      onQueryStarted: async (arg, { dispatch, getState, queryFulfilled }) => {
        const { inquiryId } = arg;

        if (!inquiryId) {
          return;
        }
        const notesCacheManager = new NotesPaginationCacheManager(
          dispatch,
          getState,
          notesApi,
        );

        const cachedArgs = inquiryApi.util.selectCachedArgsForQuery(
          getState(),
          InquiryEndpointKey.GET_INQUIRIES,
        );

        const patches = cachedArgs.map((cachedQueryArg) => {
          const patchResult = dispatch(
            inquiryApi.util.updateQueryData(
              InquiryEndpointKey.GET_INQUIRIES,
              cachedQueryArg,
              notesCacheManager.updateInquiryLastContactDate(inquiryId),
            ),
          );

          return patchResult;
        });

        try {
          const { data } = await queryFulfilled;

          dispatch(
            notesApi.util.updateQueryData(
              'getPatientInquiryNotes',
              { inquiryId },
              (draft) => {
                if (!draft?.data) return;

                notesCacheManager.handleNewNoteCache(
                  draft,
                  arg,
                  data.noteExternalId,
                );

                if (
                  draft.pageNumber === 1 &&
                  draft.data.length > draft.pageSize
                ) {
                  notesCacheManager.reloadPatientInquiryNotes(inquiryId);
                }
              },
            ),
          );
        } catch (_) {
          patches.forEach((patch) => patch.undo());
        }
      },
      query: (body) => ({
        body,
        method: HttpRequestMethod.POST,
        url: urlConstants.clinical_data.postNote,
      }),
    }),
    createAddendum: build.mutation<
      { noteAddendumId: string },
      { addendaBody: AddNoteAddendumRequest; noteExternalId: string }
    >({
      invalidatesTags: [CacheTag.GetNoteList, CacheTag.GetNote],
      query: ({ addendaBody, noteExternalId }) => ({
        body: addendaBody,
        method: HttpRequestMethod.POST,
        url: urlConstants.clinical_data.postAddendum(noteExternalId),
      }),
    }),
    deleteNote: build.mutation<{ noteExternalId: string }, DeleteNoteRequest>({
      invalidatesTags: [
        CacheTag.GetNoteList,
        CacheTag.PatientNotes,
        CacheTag.GroupClassNotesByEventExternalIds,
        CacheTag.PatientAppointmentsWithNotes,
      ],
      onQueryStarted: async (arg, { dispatch, getState, queryFulfilled }) => {
        const { inquiryId } = arg;

        if (!inquiryId) {
          return;
        }
        const notesCacheManager = new NotesPaginationCacheManager(
          dispatch,
          getState,
          notesApi,
        );

        try {
          await queryFulfilled;

          dispatch(
            notesApi.util.updateQueryData(
              'getPatientInquiryNotes',
              { inquiryId },
              () => {
                notesCacheManager.reloadPatientInquiryNotes(inquiryId);
              },
            ),
          );
        } catch (error) {
          console.error('Error deleting note:', error);
        }
      },
      query: (req) => ({
        method: HttpRequestMethod.DELETE,
        params: {
          authoredByProviderExternalId: req.authoredByProviderExternalId,
        },
        url: urlConstants.clinical_data.deleteNote(req.noteId),
      }),
      // potential for slimming this down here, but it's not a pressing performance issue
    }),
    deleteNoteAttachment: build.mutation<
      { message?: typeof DELETE_NOTE_ATTACHMENT_SUCCESS_MESSAGE },
      { noteId: string; attachmentId: string }
    >({
      query: ({ noteId, attachmentId }) => ({
        method: HttpRequestMethod.DELETE,
        url: urlConstants.clinical_data.deleteNoteAttachment(
          noteId,
          attachmentId,
        ),
      }),
    }),
    getAppointmentDetailsById: build.query<AppointmentDetailResponse, string>({
      keepUnusedDataFor: 10 * 60,
      query: (appointmentExternalId: string) => ({
        url: urlConstants.calendar.getAppointmentDetailsById(
          appointmentExternalId,
        ),
      }),
    }),
    getGroupClassNotesByEventExternalIds: build.query<
      GetGroupClassNoteByEventExternalIdResponse[],
      string[]
    >({
      providesTags: (_result, _error, arg) => {
        const tags: Array<CacheTag | { type: CacheTag; id: string }> = [
          CacheTag.GroupClassNotesByEventExternalIds,
        ];
        if (Array.isArray(arg)) {
          arg.forEach((eventId) => {
            tags.push({
              type: CacheTag.GroupClassNotesByEventExternalIds,
              id: eventId,
            });
          });
        }

        return tags;
      },
      query: (groupClassEventExternalIds) => ({
        params: { groupClassEventExternalIds },
        url: urlConstants.clinical_data.getGroupClassEventNotes,
      }),
    }),
    getGroupSectionNotesByEventExternalIds: build.query<
      GetGroupClassNoteByEventExternalIdResponse[],
      string[]
    >({
      providesTags: (_result, _error, arg) => {
        const tags: Array<CacheTag | { type: CacheTag; id: string }> = [
          CacheTag.GroupSectionNotesByEventExternalIds,
        ];
        if (Array.isArray(arg)) {
          arg.forEach((eventId) => {
            tags.push({
              type: CacheTag.GroupSectionNotesByEventExternalIds,
              id: eventId,
            });
          });
        }

        return tags;
      },
      queryFn: (groupSectionEventExternalIds, _api, _extraOptions, baseQuery) =>
        fetchInBatches<GetGroupClassNoteByEventExternalIdResponse>({
          fetchBatch: (batch) =>
            Promise.resolve(
              baseQuery({
                params: { groupSectionEventExternalIds: batch },
                url: urlConstants.clinical_data.getGroupSectionEventNotes,
              }),
            ),
          ids: groupSectionEventExternalIds,
          paramName: 'groupSectionEventExternalIds',
        }),
    }),

    getNote: build.query<NoteResponse, string>({
      providesTags: (result) =>
        result
          ? [{ id: result.noteExternalId, type: CacheTag.GetNote }]
          : [{ id: 'error', type: CacheTag.GetNote }],
      query: (noteExternalId: string) => ({
        method: HttpRequestMethod.GET,
        url: urlConstants.clinical_data.getNote(noteExternalId),
      }),
    }),
    getNoteList: build.infiniteQuery<
      PatientNoteListResponseType,
      GetNoteListRequest,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (_, __, lastPageParam, ___) => lastPageParam + 1,
      },
      keepUnusedDataFor: 5 * 60,
      providesTags: [CacheTag.GetNoteList],
      query: ({ queryArg, pageParam: pageNumber }) => {
        const { pageSize, patientId, filterBy } = queryArg;
        const params = {
          ['page-size']: pageSize,
          ['page-number']: pageNumber,
        };
        if (filterBy) {
          const queryParams: GetNoteListFilterBy = {};
          if (filterBy.createdByProviderExternalId) {
            queryParams['created-by-provider-external-id'] =
              filterBy.createdByProviderExternalId;
          }
          if (filterBy.noteStatus) {
            queryParams['note-status'] = filterBy.noteStatus;
          }
          if (filterBy.noteType) {
            queryParams['note-type'] = filterBy.noteType;
          }
          if (filterBy.providerRole) {
            queryParams['provider-role'] = filterBy.providerRole;
          }
          if (filterBy.hasAttachments) {
            queryParams['has-attachments'] = filterBy.hasAttachments;
          }
          if (filterBy.documentId) {
            queryParams['document-id'] = filterBy.documentId;
          }
          if (filterBy.title) {
            queryParams.title = filterBy.title;
          }
          if (filterBy.appointmentExternalId) {
            queryParams.appointment_external_id =
              filterBy.appointmentExternalId;
          }
          params['filter-by'] = joinQueryParams(queryParams);
        }
        return {
          method: HttpRequestMethod.GET,
          params,
          url: urlConstants.clinical_data.getNotes(patientId),
        };
      },
    }),
    getNoteTemplateAutopopulateData: build.query<
      Record<string, object>,
      GetNoteTemplateAutopopulateDataRequest
    >({
      query: ({
        patientId,
        providerId,
        templateRevisionId,
        chartOnly = false,
      }) => ({
        method: HttpRequestMethod.GET,
        params: {
          'chart-only': chartOnly,
          'patient-id': patientId,
          'provider-id': providerId,
        },
        url: urlConstants.clinical_data.getNoteTemplateAutopopulateData(
          templateRevisionId,
        ),
      }),
    }),
    getPatientInquiryNotes: build.query<
      GetPatientInquiryNotesResponse,
      GetPatientNotesRequest
    >({
      forceRefetch({ currentArg, previousArg }) {
        return currentArg.notesPageNumber !== previousArg.notesPageNumber;
      },
      merge: (currentCache, newData) => {
        if (!newData) return currentCache;
        if (!currentCache) {
          return { ...newData };
        }
        if (currentCache.pageNumber != newData.pageNumber) {
          const existingNotes = new Map(
            currentCache.data.map((note) => [note.noteExternalId, note]),
          );

          newData.data.forEach((note) => {
            existingNotes.set(note.noteExternalId, note);
          });

          currentCache.data = Array.from(existingNotes.values());
          currentCache.count = newData.count;
          currentCache.pageNumber = newData.pageNumber;
          currentCache.pageSize = newData.pageSize;
          return currentCache;
        }

        return newData;
      },
      providesTags: (result, error, arg) => [
        { id: arg.inquiryId, type: CacheTag.Inquiry },
        { id: arg.inquiryId, type: CacheTag.PatientInquiryNotes },
      ],
      query: ({
        inquiryId,
        notesPageSize,
        notesPageNumber,
        patientExternalIds,
        filterByAppointmentExternalIds,
      }) => ({
        params: {
          'filter-by': filterByAppointmentExternalIds
            ? 'appointment-external-ids::' +
              filterByAppointmentExternalIds.join(',')
            : undefined,
          'inquiry-external-id': inquiryId,
          'page-number': notesPageNumber,
          'page-size': notesPageSize,
          patientExternalIds,
        },
        url: urlConstants.clinical_data.getInquiryNotesForPatient,
      }),
      serializeQueryArgs: ({ queryArgs }) => {
        return queryArgs.inquiryId;
      },
    }),
    getPatientNotes: build.query<
      GetPatientInquiryNotesResponse,
      GetPatientNotesRequest
    >({
      providesTags: (result, error, arg) => [
        { id: arg.inquiryId, type: CacheTag.Inquiry },
        CacheTag.PatientNotes,
      ],
      query: ({
        inquiryId,
        notesPageSize,
        notesPageNumber,
        patientExternalIds,
        filterByAppointmentExternalIds,
      }) => ({
        params: {
          'filter-by': filterByAppointmentExternalIds
            ? 'appointment-external-ids::' +
              filterByAppointmentExternalIds.join(',')
            : undefined,
          'inquiry-external-id': inquiryId,
          'page-number': notesPageNumber,
          'page-size': notesPageSize,
          patientExternalIds,
        },
        url: urlConstants.clinical_data.getNotesForPatient,
      }),
    }),
    updateVoidReason: build.mutation({
      onQueryStarted: async (
        { data },
        { getState, dispatch, queryFulfilled },
      ) => {
        try {
          await queryFulfilled;
          const { inquiryId } = data;
          if (!inquiryId) {
            return;
          }
          const notesCacheManager = new NotesPaginationCacheManager(
            dispatch,
            getState,
            notesApi,
          );
          dispatch(
            notesApi.util.updateQueryData(
              'getPatientInquiryNotes',
              { inquiryId },
              (draft) => {
                if (!draft?.data) return;
                notesCacheManager.handleVoidNoteCache(
                  draft,
                  inquiryId,
                  data.noteExternalId,
                );
              },
            ),
          );
        } catch (error) {
          console.error('Error updating notes:', error);
        }
      },
      query: ({ data }) => ({
        body: { voidReason: data.voidReason },
        method: HttpRequestMethod.PUT,
        url: urlConstants.clinical_data.putNoteVoidReason(data.noteExternalId),
        responseHandler: async (response) => {
          if (response.ok) {
            const responseText = await response.text();
            return responseText;
          } else {
            const errorData = await response.json();
            return errorData;
          }
        },
      }),
      invalidatesTags: [
        CacheTag.PatientNotes,
        CacheTag.PatientAppointmentsWithNotes,
        CacheTag.GetNoteList,
      ],
    }),
    updateGroupClassNote: build.mutation<
      { noteExternalId: string },
      {
        body: UpdateGroupClassEventNoteRequest;
        options?: { skipInvalidatingGetNote?: boolean };
      }
    >({
      invalidatesTags: (result, _, { options, body }) =>
        result
          ? [
              body.groupSectionEventExternalId
                ? CacheTag.GroupSectionNotesByEventExternalIds
                : CacheTag.GroupClassNotesByEventExternalIds,
              !options?.skipInvalidatingGetNote && {
                id: result.noteExternalId,
                type: CacheTag.GetNote,
              },
            ]
          : [],
      query: ({ body }) => ({
        body,
        method: HttpRequestMethod.PATCH,
        url: body.groupSectionEventExternalId
          ? urlConstants.clinical_data.putGroupSectionEventNote(
              body.noteExternalId,
            )
          : urlConstants.clinical_data.putGroupNote(body.noteExternalId),
      }),
    }),
    updateNote: build.mutation<
      {
        activityDate: string;
        noteExternalId: string;
      },
      {
        body: UpdateNoteRequest;
        options?: { skipInvalidatingGetNote?: boolean };
      }
    >({
      invalidatesTags: (result, _, { options }) => {
        const tags: (CacheTag | { type: CacheTag; id: string })[] = [];
        if (result) {
          tags.push(
            CacheTag.PatientNotes,
            CacheTag.PatientAppointmentsWithNotes,
            CacheTag.GetNoteList,
            CacheTag.ProviderDashboard,
          );
        }
        if (!options?.skipInvalidatingGetNote) {
          tags.push({
            id: result.noteExternalId,
            type: CacheTag.GetNote,
          });
        }
        return tags;
      },
      onQueryStarted: async (
        { body },
        { getState, dispatch, queryFulfilled },
      ) => {
        try {
          await queryFulfilled;
          const { inquiryId } = body;

          if (!inquiryId) {
            return;
          }
          const notesCacheManager = new NotesPaginationCacheManager(
            dispatch,
            getState,
            notesApi,
          );
          dispatch(
            notesApi.util.updateQueryData(
              'getPatientInquiryNotes',
              { inquiryId },
              (draft) => {
                if (!draft?.data) return;

                if (body.noteStatus === 'void') {
                  notesCacheManager.handleVoidNoteCache(
                    draft,
                    inquiryId,
                    body.noteExternalId,
                  );
                  if (
                    draft.pageNumber === 1 &&
                    draft.data.length < draft.pageSize
                  ) {
                    notesCacheManager.reloadPatientInquiryNotes(inquiryId);
                  }
                  return;
                }

                notesCacheManager.handleNewNoteCache(
                  draft,
                  body,
                  body.noteExternalId,
                );
                if (
                  draft.pageNumber === 1 &&
                  draft.data.length > draft.pageSize
                ) {
                  notesCacheManager.reloadPatientInquiryNotes(inquiryId);
                }
              },
            ),
          );
        } catch (error) {
          console.error('Error updating notes:', error);
        }
      },

      query: ({ body }) => ({
        body,
        method: HttpRequestMethod.PUT,
        url: urlConstants.clinical_data.putNote,
      }),
    }),
  }),
});

export const {
  useGetNoteQuery,
  useGetNoteListInfiniteQuery,
  useGetGroupClassNotesByEventExternalIdsQuery,
  useLazyGetGroupClassNotesByEventExternalIdsQuery,
  useGetAppointmentDetailsByIdQuery,
  useCreateNoteMutation,
  useCreateAddendumMutation,
  useCreateGroupClassNoteMutation,
  useCreateGroupSectionEventNoteMutation,
  useGetPatientNotesQuery,
  useLazyGetPatientInquiryNotesQuery,
  useUpdateNoteMutation,
  useUpdateGroupClassNoteMutation,
  useDeleteNoteMutation,
  useDeleteNoteAttachmentMutation,
  useLazyGetNoteTemplateAutopopulateDataQuery,
  useUpdateVoidReasonMutation,
  useGetGroupSectionNotesByEventExternalIdsQuery,
} = notesApi;
